import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';
import './DeckIconManager.css';

const API_BASE_URL = '/api';


interface DeckIconManagerProps {
    deckId: string;
    currentIconUrl?: string | null;
    onIconUpdate: () => void;
    onClose: () => void;
}

const DeckIconManager: React.FC<DeckIconManagerProps> = ({
    deckId,
    currentIconUrl,
    onIconUpdate,
    onClose
}) => {
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(
        currentIconUrl || null
    );
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validação de tipo
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setError('Formato inválido. Use PNG, JPG, SVG ou WEBP');
            return;
        }

        // Validação de tamanho (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Arquivo muito grande. Máximo: 5MB');
            return;
        }

        setError(null);
        setIconFile(file);

        // Preview
        const reader = new FileReader();
        reader.onloadend = () => setIconPreview(reader.result as string);
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!iconFile) return;

        setUploading(true);
        setError(null);

        try {
            const token = localStorage.getItem('accessToken');
            const formData = new FormData();
            formData.append('icon', iconFile);

            // Upload do ícone
            const uploadRes = await fetch(`${API_BASE_URL}/upload/deck-icon/${deckId}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (!uploadRes.ok) {
                throw new Error('Erro no upload do ícone');
            }

            const { iconUrl } = await uploadRes.json();

            // Atualizar deck com novo iconUrl
            await fetch(`${API_BASE_URL}/decks/${deckId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ iconUrl })
            });

            onIconUpdate();
            onClose();
        } catch (err) {
            setError('Falha ao salvar ícone. Tente novamente.');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!currentIconUrl) return;
        if (!window.confirm('Remover ícone do deck?')) return;

        setUploading(true);
        setError(null);

        try {
            const token = localStorage.getItem('accessToken');

            // Remover arquivo do servidor
            await fetch(`${API_BASE_URL}/upload/deck-icon/${deckId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ iconUrl: currentIconUrl })
            });

            // Atualizar deck removendo iconUrl
            await fetch(`${API_BASE_URL}/decks/${deckId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ iconUrl: null })
            });

            onIconUpdate();
            onClose();
        } catch (err) {
            setError('Erro ao remover ícone');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="dim-overlay" onClick={onClose}>
            <div className="dim-modal" onClick={(e) => e.stopPropagation()}>
                <button className="dim-close-btn" onClick={onClose} title="Fechar">
                    <X size={20} />
                </button>

                <h2>Ícone do Deck</h2>

                {/* Preview do Ícone */}
                {iconPreview && (
                    <div className="dim-preview-container">
                        <img src={iconPreview} alt="Preview" className="dim-preview-img" />
                    </div>
                )}

                {/* Upload */}
                <div className="dim-upload-area">
                    <label htmlFor="icon-upload" className="dim-upload-label">
                        <Upload size={20} />
                        {iconFile ? iconFile.name : 'Escolher arquivo'}
                    </label>
                    <input
                        id="icon-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="dim-input-hidden"
                    />
                </div>

                {/* Mensagem de Erro */}
                {error && (
                    <div className="dim-error">
                        {error}
                    </div>
                )}

                {/* Ações */}
                <div className="dim-actions">
                    <button onClick={onClose} className="dim-btn-secondary">
                        Cancelar
                    </button>

                    {currentIconUrl && (
                        <button
                            onClick={handleRemove}
                            className="dim-btn-danger"
                            disabled={uploading}
                        >
                            Remover Ícone
                        </button>
                    )}

                    <button
                        onClick={handleUpload}
                        className="dim-btn-primary"
                        disabled={!iconFile || uploading}
                    >
                        {uploading ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeckIconManager;